import React, { createContext, useState, useContext } from 'react';
import {
  LOGIN_POST,
  TOKEN_VALIDATE_GET,
  DOCS_GET,
  USER_LOGOUT,
  GET_USER_INFO,
} from './api';
import { useLocation, useNavigate } from 'react-router-dom';

interface UserContextData {
  error: string | null;
  login: boolean | null;
  loading: boolean | undefined;
  isAuthLoading: boolean | null;
  data: [];
  userLoginToken: (tokenLink: string) => Promise<void>;
  clinicas:
    | [
        {
          id: number;
          nome: string;
          contato: string;
        },
      ]
    | undefined;
  clinica: { id: number; nome: string; contato: string } | undefined;
  nome: string;
  setClinica: React.Dispatch<
    React.SetStateAction<
      { id: number; nome: string; contato: string } | undefined
    >
  >;
  token: string | undefined;
  email: string | undefined;
  userLogout: () => Promise<void>;
  userLogin: (username: string, password: string) => Promise<void>;
  getUser: (token: string) => Promise<void>;
  getUserInfo: (token: string) => Promise<void>;
  handleRefresh: () => Promise<void>; // Adiciona handleRefresh ao contexto
}

interface UserProviderProps {
  children: React.ReactNode;
}

export const UserContext = createContext<UserContextData>(
  {} as UserContextData,
);

export const useUserContext = () => useContext(UserContext);

export const UserStorage = ({ children }: UserProviderProps) => {
  const [data, setData] = React.useState<any>();
  const [nome, setNome] = React.useState<any>();
  const [login, setLogin] = React.useState<boolean | null>(null);
  const [isAuthLoading, setIsAuthLoading] = React.useState(true);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [id, setId] = React.useState();
  const [email, setEmail] = React.useState();
  const [token, setToken] = React.useState<string>();
  const [clinicas, setClinicas] = React.useState();
  const [clinica, setClinica] = React.useState<
    { id: number; nome: string; contato: string } | undefined
  >(undefined);
  const location = useLocation();
  const navigate = useNavigate();

  // Função para validar o token
  async function validateToken(token: string) {
    try {
      const { url, options } = TOKEN_VALIDATE_GET(token);
      const response = await fetch(url, options);
      if (!response.ok) throw new Error('Token inválido');
      return true;
    } catch (err) {
      throw new Error('Token inválido');
    }
  }

  // Função para buscar as informações do usuário
  async function getUserInfo(token: string) {
    try {
      const { url, options } = GET_USER_INFO(token);
      const response = await fetch(url, options);

      if (!response.ok) {
        const error: any = new Error('Erro ao buscar informações do usuário');
        error.status = response.status; // 🔥 preserva o status (ex: 401)
        throw error;
      }

      const userData = await response.json();
      setNome(userData.nome);
      setEmail(userData.email);
      setId(userData.id);
      setClinicas(userData.cliente);
      console.log(userData.cliente);
    } catch (err: any) {
      // 🔥 Se já veio com status, apenas repassa
      if (err?.status) {
        throw err;
      }

      // Erro inesperado (rede, JS, etc)
      const error: any = new Error('Erro ao buscar informações do usuário');
      error.status = 500;
      throw error;
    }
  }

  // Função para buscar os documentos do usuário
  async function getUser(token: string) {
    try {
      setError(null);
      setLoading(true);
      const { url, options } = DOCS_GET(token);
      const response = await fetch(url, options);
      const json = await response.json();
      setData(json);
      setLogin(true);
      if (!location.pathname.includes('/home')) navigate('/home');
    } catch (err: any) {
      setError(err.message);
      setLogin(false);
    } finally {
      setLoading(false);
    }
  }

  async function userLoginToken(tokenLink: string) {
    try {
      setLoading(true);
      setError(null);

      // Salva token no estado e no localStorage
      setToken(tokenLink);
      window.localStorage.setItem('token', tokenLink);

      // Carrega infos do usuário
      await getUserInfo(tokenLink);

      // Carrega documentos e redireciona para /home
      await getUser(tokenLink);

      setLogin(true);
    } catch (err: any) {
      setLogin(false);

      // mantém erro no contexto, se você usa em outros lugares
      setError(err?.message || 'Erro ao realizar login.');

      // 🔥 REPASSA o erro para quem chamou (AcessoRapido)
      throw err;
    } finally {
      setLoading(false);
    }
  }

  // Função para fazer login
  async function userLogin(username: string, password: string) {
    try {
      setLoading(true);
      setError(null);
      // Verifica se os campos foram preenchidos
      if (!username || !password) {
        throw new Error('Campo usuário e/ou senha não foi preenchido!');
      }

      const { url, options } = LOGIN_POST({ cpf: username, senha: password });
      const tokenRes = await fetch(url, options);
      if (!tokenRes.ok) {
        throw new Error('Usuário ou senha incorretos!');
      }
      const { id, nome, email, cliente } = await tokenRes.json();

      setNome(nome);
      setClinicas(cliente);
      setId(id);
      setEmail(email);

      const token: string | null = tokenRes.headers.get('Authorization');
      if (token) {
        setToken(token);
        window.localStorage.setItem('token', token); // Armazena o token no localStorage
        await getUserInfo(token); // Busca as informações do usuário
        await getUser(token); // Busca os documentos do usuário
      }
    } catch (err: any) {
      setError(err.message);
      console.log(err.message);
      setLogin(false);
    } finally {
      setLoading(false);
    }
  }

  // Função para fazer logout
  async function sair() {
    try {
      if (!id || !token) {
        console.warn('Logout sem id ou token - apenas limpando estado local');
        return false;
      }

      const { url, options } = USER_LOGOUT({ id, token }, token);
      const response = await fetch(url, options);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message = errorData.message || 'Resposta não-OK sem mensagem';
        console.error('Erro HTTP no logout:', response.status, message);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Erro no processo de logout:', err);
      return false;
    }
  }

  const userLogout = React.useCallback(
    async function () {
      try {
        // Só tenta fazer logout na API se tiver token e id
        if (token && id) {
          await sair();
        }

        // Limpa os estados independentemente do logout na API
        setData(null);
        setError(null);
        setLoading(false);
        setLogin(false);
        setClinicas(undefined);
        setClinica(undefined);
        window.localStorage.removeItem('token');
        navigate('/');
      } catch (err) {
        console.error('Erro no logout:', err);
        // Mesmo que o logout na API falhe, limpa os estados locais
        setData(null);
        setError(null);
        setLoading(false);
        setLogin(false);
        setClinicas(undefined);
        setClinica(undefined);
        window.localStorage.removeItem('token');
        navigate('/');
      }
    },
    [navigate, id, token],
  );

  // Função para atualizar os dados (refresh)
  async function handleRefresh() {
    if (token) {
      try {
        setLoading(true);
        await getUser(token); // Busca os documentos do usuário novamente
      } catch (error) {
        console.error('Erro ao buscar documentos:', error);
      } finally {
        setLoading(false);
      }
    }
  }

  // Efeito para autologin ao carregar a aplicação
  React.useEffect(() => {
    async function autologin() {
      const tokenCache = window.localStorage.getItem('token');
      if (tokenCache) {
        try {
          setError(null);
          setLoading(true);

          // Valida o token antes de buscar os documentos
          await validateToken(tokenCache);
          setToken(tokenCache); // Atualiza o estado do token
          await getUserInfo(tokenCache); // Busca as informações do usuário (incluindo o id)
          await getUser(tokenCache); // Busca os documentos do usuário
        } catch (err) {
          console.error('Erro no autologin:', err);
          // Limpa os estados sem tentar fazer logout na API
          setData(null);
          setError(null);
          setLoading(false);
          setLogin(false);
          setClinicas(undefined);
          setClinica(undefined);
          window.localStorage.removeItem('token');
          navigate('/');
        } finally {
          setIsAuthLoading(false);
        }
      } else {
        setLogin(false);
        setIsAuthLoading(false);
      }
    }
    autologin();
  }, [navigate]); // Removemos userLogout das dependências

  return (
    <UserContext.Provider
      value={{
        error,
        isAuthLoading,
        loading,
        login,
        data,
        userLogout,
        userLogin,
        userLoginToken,
        clinicas,
        setClinica,
        clinica,
        getUser,
        token,
        nome,
        email,
        getUserInfo,
        handleRefresh, // Adiciona handleRefresh ao contexto
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
